import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Plus, X, Link as LinkIcon, Loader2 } from "lucide-react";

/**
 * ProductivityMarkDialog — daily self-report of output.
 *
 * Iter 42.5.4 — project picker removed per user ask. The productivity
 * mark is now user-level: user selects a profile (Writer / AI Writer /
 * Editor / Publisher) that they hold ANYWHERE across their projects,
 * enters the count + supporting links. Backend still validates the user
 * has at least one project assignment matching the profile. Theme is a
 * soft purple gradient (was emerald/teal — user ask).
 */
const PROFILE_META = {
  human_writer: {
    label:       "Human Writer",
    countLabel:  "Total Word Count",
    countPlaceholder: "e.g. 2400",
    linksLabel:  "Doc links",
    linksPlaceholder: "https://docs.google.com/…",
    requireLinks: true,
    step: 1, min: 0,
  },
  ai_writer: {
    label:       "AI Writer",
    countLabel:  "Content Created (count)",
    countPlaceholder: "e.g. 5",
    linksLabel:  "Doc links",
    linksPlaceholder: "https://docs.google.com/…",
    requireLinks: true,
    step: 1, min: 0,
  },
  editor: {
    label:       "Editor",
    countLabel:  "Content Edited (may be decimal)",
    countPlaceholder: "e.g. 2.5",
    linksLabel:  "Doc links",
    linksPlaceholder: "https://docs.google.com/…",
    requireLinks: true,
    step: 0.1, min: 0,
  },
  publisher: {
    label:       "Publisher",
    countLabel:  "Content Published",
    countPlaceholder: "e.g. 3",
    linksLabel:  "Post links",
    linksPlaceholder: "https://yoursite.com/blog/post-slug",
    requireLinks: true,
    step: 1, min: 0,
  },
};

export default function ProductivityMarkDialog({ open, onClose, onCreated }) {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState("");
  const [count, setCount] = useState("");
  const [links, setLinks] = useState([""]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCount(""); setLinks([""]); setSelectedProfile("");
    setLoading(true);
    api.get("/productivity/my-profiles")
      .then(({ data }) => {
        const p = data?.profiles || [];
        setProfiles(p);
        if (p.length === 1) setSelectedProfile(p[0].value);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open]);

  const meta = useMemo(() => PROFILE_META[selectedProfile] || null, [selectedProfile]);

  const setLinkAt = (i, v) => setLinks((prev) => prev.map((x, idx) => (idx === i ? v : x)));
  const addLink = () => setLinks((prev) => [...prev, ""]);
  const removeLink = (i) => setLinks((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!selectedProfile) { toast.error("Pick a profile"); return; }
    const parsed = Number(count);
    if (Number.isNaN(parsed) || parsed < 0) { toast.error(`${meta.countLabel} must be a positive number`); return; }
    const cleanLinks = (links || []).map((l) => (l || "").trim()).filter(Boolean);
    if (meta.requireLinks && cleanLinks.length === 0) {
      toast.error(`Please add at least one ${meta.linksLabel.toLowerCase()}`); return;
    }
    setSaving(true);
    try {
      await api.post("/productivity/marks", {
        profile: selectedProfile,
        count: parsed,
        links: cleanLinks,
      });
      toast.success(`Productivity logged for today (${meta.label}: ${parsed})`);
      onCreated && onCreated();
      onClose && onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
      data-testid="productivity-mark-dialog"
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Iter 42.5.4 — soft purple gradient theme (user ask). */}
        <div className="px-6 py-4 border-b border-purple-100 bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50">
          <div className="flex items-center gap-2 text-purple-800 font-semibold">
            <Sparkles className="h-5 w-5" /> Mark My Productivity
          </div>
          <div className="text-xs text-slate-600 mt-1">
            Logging today's output. Date is captured automatically.
          </div>
        </div>

        <div className="px-6 py-4 space-y-4 max-h-[65vh] overflow-y-auto scrollbar-thin">
          {loading ? (
            <div className="text-center text-slate-400 text-sm py-6" data-testid="productivity-mark-loading">
              <Loader2 className="h-4 w-4 mx-auto animate-spin" /> Loading your roles…
            </div>
          ) : profiles.length === 0 ? (
            <div className="text-center text-slate-500 text-sm py-6" data-testid="productivity-mark-empty">
              You're not assigned as a Writer, Editor, or Publisher on any project yet — nothing to log.
            </div>
          ) : (
            <>
              {profiles.length > 1 && (
                <div>
                  <div className="text-xs font-medium text-slate-700 mb-1">Log as</div>
                  <div className="grid grid-cols-2 gap-2">
                    {profiles.map((p) => (
                      <button
                        key={p.value}
                        type="button"
                        onClick={() => setSelectedProfile(p.value)}
                        className={`text-xs px-3 py-2 rounded-lg border text-left transition ${
                          selectedProfile === p.value
                            ? "bg-purple-50 border-purple-300 text-purple-800 font-medium"
                            : "bg-white border-slate-200 hover:bg-slate-50 text-slate-700"
                        }`}
                        data-testid={`mark-profile-${p.value}`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {meta && (
                <>
                  <div>
                    <label className="text-xs font-medium text-slate-700" htmlFor="mark-count">
                      {meta.countLabel}
                    </label>
                    <Input
                      id="mark-count"
                      type="number"
                      step={meta.step}
                      min={meta.min}
                      value={count}
                      onChange={(e) => setCount(e.target.value)}
                      placeholder={meta.countPlaceholder}
                      className="mt-1"
                      data-testid="mark-count-input"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-slate-700">{meta.linksLabel}</label>
                      <button
                        type="button"
                        onClick={addLink}
                        className="text-[11px] text-purple-700 hover:underline inline-flex items-center gap-1"
                        data-testid="mark-add-link"
                      >
                        <Plus className="h-3 w-3" /> Add another
                      </button>
                    </div>
                    <div className="mt-1 space-y-1.5">
                      {links.map((v, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <div className="relative flex-1">
                            <LinkIcon className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                            <Input
                              type="url"
                              value={v}
                              onChange={(e) => setLinkAt(i, e.target.value)}
                              placeholder={meta.linksPlaceholder}
                              className="pl-8"
                              data-testid={`mark-link-${i}`}
                            />
                          </div>
                          {links.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLink(i)}
                              className="text-slate-400 hover:text-rose-600 p-1"
                              title="Remove"
                              data-testid={`mark-link-remove-${i}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="mark-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || loading || profiles.length === 0 || !selectedProfile}
            className="bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white shadow-sm"
            data-testid="mark-submit"
          >
            {saving ? "Saving…" : "Mark Productivity"}
          </Button>
        </div>
      </div>
    </div>
  );
}
